/**
 * Resolves Create RMA label-fee UI options from RefundResolutionCapability,
 * mirroring policy-rule additional-payments charge and shipping-label-fee enforcement.
 */

import { IOption } from "../../../components/inputs/select-dropdown/select-dropdown";
import { IRefundResolutionCapability, PROFILE_CAPABILITIES } from "../../../models/ProfileCapabilities";
import dashboardSettingsStore from "../../../stores/dashboard-settings-store";
import { CHARGE_FEES } from "../../policies/constants";
import { FEE_TYPE, FEE_TYPE_FREE } from "../../policies/policy-types";

export interface ICreateRmaLabelFeeOption {
	label: string;
	value: FEE_TYPE;
	disabled?: boolean;
}

export type LabelFeeTypeOption = Pick<ICreateRmaLabelFeeOption, "value" | "disabled">;

export interface ICreateRmaLabelFeeCapabilityFields {
	isReturnFeeSupported: boolean;
	supportedShippingLabelFeeTypes: FEE_TYPE[];
	supportedChargeFeesOptions: CHARGE_FEES[];
}

export interface ILabelFeeChargeEnforcement {
	isForced: boolean;
	isDisabled: boolean;
	forcedIsCharged: boolean | null;
}

export interface ICreateRmaLabelFeeCapabilityEnforcement extends ILabelFeeChargeEnforcement {
	isTypeEnforced: boolean;
	enforcedFeeType: FEE_TYPE | null;
	isChargeToggleDisabled: boolean;
}

export interface ICreateRmaLabelFeeState {
	isCharged: boolean;
	feeType: FEE_TYPE | null | undefined;
	isPolicyLedMode?: boolean;
}

export interface ICreateRmaLabelFeeActions {
	setCharged: (value: boolean) => void;
	setFeeType: (value: FEE_TYPE | null) => void;
}

export function getCreateRmaLabelFeeCapability(): ICreateRmaLabelFeeCapabilityFields {
	const capability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.REFUND_RESOLUTION);

	return {
		isReturnFeeSupported: capability.isReturnFeeSupported === true,
		supportedShippingLabelFeeTypes: getSupportedShippingLabelFeeTypesFromCapability(capability),
		supportedChargeFeesOptions: capability.supportedChargeFeesOptions ?? [],
	};
}

export function getSupportedShippingLabelFeeTypes(): FEE_TYPE[] {
	return getCreateRmaLabelFeeCapability().supportedShippingLabelFeeTypes;
}

function getSupportedShippingLabelFeeTypesFromCapability(
	capability: IRefundResolutionCapability,
): FEE_TYPE[] {
	return (capability.supportedShippingLabelFeeTypes ?? []).filter(
		(type): type is FEE_TYPE => type != null,
	) as FEE_TYPE[];
}

export function getFirstSupportedChargeFeesOption(
	supportedChargeFeesOptions: CHARGE_FEES[],
): CHARGE_FEES {
	return supportedChargeFeesOptions[0] ?? CHARGE_FEES.DO_NOT_CHARGE_FEE;
}

export function isLabelFeeChargeEnabled(supportedChargeFeesOptions: CHARGE_FEES[]): boolean {
	return getFirstSupportedChargeFeesOption(supportedChargeFeesOptions) === CHARGE_FEES.CHARGE_FEE;
}

export function isLabelFeeChargeForced(supportedChargeFeesOptions: CHARGE_FEES[]): boolean {
	return supportedChargeFeesOptions.length === 1;
}

export function isLabelFeeChargeDisabled(supportedChargeFeesOptions: CHARGE_FEES[]): boolean {
	return (
		supportedChargeFeesOptions.length === 1 &&
		supportedChargeFeesOptions[0] === CHARGE_FEES.DO_NOT_CHARGE_FEE
	);
}

export function getLabelFeeChargeEnforcement(
	supportedChargeFeesOptions: CHARGE_FEES[],
): ILabelFeeChargeEnforcement {
	const isForced = isLabelFeeChargeForced(supportedChargeFeesOptions);
	const isDisabled = isLabelFeeChargeDisabled(supportedChargeFeesOptions);
	const firstOption = getFirstSupportedChargeFeesOption(supportedChargeFeesOptions);

	return {
		isForced,
		isDisabled,
		forcedIsCharged: isForced
			? firstOption === CHARGE_FEES.CHARGE_FEE
			: isDisabled
				? false
				: null,
	};
}

export function hasRestrictedShippingLabelFeeTypes(supportedTypes: FEE_TYPE[]): boolean {
	return supportedTypes.length > 0;
}

export function filterShippingLabelFeeOptions<T extends { value: FEE_TYPE }>(
	options: T[],
	supportedTypes: FEE_TYPE[],
): T[] {
	return options.filter((option) => supportedTypes.includes(option.value));
}

export function filterSettingsLabelFeeTypeOptions(
	options: IOption[],
	supportedTypes: FEE_TYPE[],
	supportedChargeFeesOptions: CHARGE_FEES[],
): IOption[] {
	const allowFree = supportedChargeFeesOptions.includes(CHARGE_FEES.DO_NOT_CHARGE_FEE);

	return options.filter((option) => {
		if (option.value === FEE_TYPE_FREE) {
			return allowFree;
		}

		return supportedTypes.includes(option.value as FEE_TYPE);
	});
}

export function isLabelFeeTypeEnforced(chargedOptions: LabelFeeTypeOption[]): boolean {
	return chargedOptions.length === 1;
}

export function getEnforcedLabelFeeType(chargedOptions: LabelFeeTypeOption[]): FEE_TYPE | null {
	if (!isLabelFeeTypeEnforced(chargedOptions)) {
		return null;
	}

	return chargedOptions[0].value;
}

export function getGodModeLabelFeeEnforcement(isLabelCostSupported: boolean): {
	isEnforced: boolean;
	enforcedFeeType: FEE_TYPE | null;
} {
	const options = buildGodModeShippingLabelFeeOptions(isLabelCostSupported);

	return {
		isEnforced: isLabelFeeTypeEnforced(options),
		enforcedFeeType: getEnforcedLabelFeeType(options),
	};
}

export function getSettingsLabelFeeEnforcement(
	chargedOptions: Array<{ value: string | number; disabled?: boolean }>,
): {
	isEnforced: boolean;
	enforcedFeeType: FEE_TYPE | null;
} {
	const typeOptions: LabelFeeTypeOption[] = chargedOptions.map((option) => ({
		value: option.value as FEE_TYPE,
		disabled: option.disabled,
	}));

	return {
		isEnforced: isLabelFeeTypeEnforced(typeOptions),
		enforcedFeeType: getEnforcedLabelFeeType(typeOptions),
	};
}

export function getCreateRmaLabelFeeCapabilityEnforcement(
	isLabelCostSupported: boolean,
): ICreateRmaLabelFeeCapabilityEnforcement {
	const { supportedChargeFeesOptions } = getCreateRmaLabelFeeCapability();
	const chargeEnforcement = getLabelFeeChargeEnforcement(supportedChargeFeesOptions);
	const typeEnforcement = getGodModeLabelFeeEnforcement(isLabelCostSupported);

	return {
		...chargeEnforcement,
		isTypeEnforced: typeEnforcement.isEnforced,
		enforcedFeeType: typeEnforcement.enforcedFeeType,
		isChargeToggleDisabled: chargeEnforcement.isForced || chargeEnforcement.isDisabled,
	};
}

export function applyCreateRmaLabelFeeCapabilityDefaults(
	state: ICreateRmaLabelFeeState,
	actions: ICreateRmaLabelFeeActions,
	enforcement: ICreateRmaLabelFeeCapabilityEnforcement,
): void {
	if (state.isPolicyLedMode) {
		return;
	}

	if (enforcement.forcedIsCharged !== null && state.isCharged !== enforcement.forcedIsCharged) {
		actions.setCharged(enforcement.forcedIsCharged);
		return;
	}

	const shouldApplyFeeType =
		enforcement.isTypeEnforced &&
		enforcement.enforcedFeeType &&
		(enforcement.isForced || state.isCharged);

	if (shouldApplyFeeType && state.feeType !== enforcement.enforcedFeeType) {
		actions.setFeeType(enforcement.enforcedFeeType);
	}
}

export function buildGodModeShippingLabelFeeOptions(
	isLabelCostSupported: boolean,
): ICreateRmaLabelFeeOption[] {
	const baseOptions: ICreateRmaLabelFeeOption[] = [
		{
			label: "FEE_TYPE_COST",
			value: FEE_TYPE.COST,
			disabled: !isLabelCostSupported,
		},
		{
			label: "FEE_TYPE_COST_PLUS_ITEM_FEE",
			value: FEE_TYPE.COST_PLUS_ITEM_FEE,
			disabled: !isLabelCostSupported,
		},
		{
			label: "FEE_TYPE_COST_PLUS_SHIPMENT_FEE",
			value: FEE_TYPE.COST_PLUS_SHIPMENT_FEE,
			disabled: !isLabelCostSupported,
		},
		{
			label: "FEE_TYPE_SHIPMENT_FEE",
			value: FEE_TYPE.SHIPMENT_FEE,
		},
	];

	return filterShippingLabelFeeOptions(baseOptions, getSupportedShippingLabelFeeTypes());
}
